import { useState, useEffect } from 'react';
import {
  Package,
  MapPin,
  Clock,
  CheckCircle2,
  Play,
  User,
  RefreshCw,
  ListChecks,
  MoreHorizontal,
  AlertCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { yardApi, YardPickList, YardPickTask } from '@/api/services/yard.api';
import { PageHeader } from '@/components/common/PageHeader';
import { MetricCard } from '@/components/common/MetricCard';

type PickListStatus = 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'PARTIALLY_COMPLETED' | 'CANCELLED';

export default function YardPicking() {
  const [pickLists, setPickLists] = useState<YardPickList[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    pending: 0,
    inProgress: 0,
    completedToday: 0,
    totalTasks: 0,
  });

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Selected pick list for details
  const [selectedPickList, setSelectedPickList] = useState<YardPickList | null>(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  // Complete task dialog
  const [completeTaskDialogOpen, setCompleteTaskDialogOpen] = useState(false);
  const [taskToComplete, setTaskToComplete] = useState<YardPickTask | null>(null);
  const [completeTaskForm, setCompleteTaskForm] = useState({
    pickedQty: '',
    notes: '',
  });

  useEffect(() => {
    fetchPickLists();
    fetchStats();
  }, [statusFilter]);

  const fetchPickLists = async () => {
    try {
      setLoading(true);
      const params: Record<string, string> = {};
      if (statusFilter !== 'all') params.status = statusFilter;

      const response = await yardApi.picking.getAll(params);
      setPickLists(response.data);
    } catch (error) {
      console.error('Error fetching pick lists:', error);
      toast.error('Failed to load pick lists');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await yardApi.picking.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleStartPickList = async (pickList: YardPickList) => {
    try {
      await yardApi.picking.start(pickList.id);
      toast.success('Picking started');
      fetchPickLists();
      fetchStats();
      // Refresh selected if viewing
      if (selectedPickList?.id === pickList.id) {
        const response = await yardApi.picking.getById(pickList.id);
        setSelectedPickList(response);
      }
    } catch (error) {
      console.error('Error starting pick list:', error);
      toast.error('Failed to start picking');
    }
  };

  const handleOpenCompleteTaskDialog = (task: YardPickTask) => {
    setTaskToComplete(task);
    setCompleteTaskForm({
      pickedQty: task.requestedQty.toString(),
      notes: '',
    });
    setCompleteTaskDialogOpen(true);
  };

  const handleCompleteTask = async () => {
    if (!taskToComplete || !selectedPickList) return;

    const pickedQty = parseFloat(completeTaskForm.pickedQty);
    if (isNaN(pickedQty) || pickedQty < 0) {
      toast.error('Please enter a valid quantity');
      return;
    }

    try {
      await yardApi.picking.completeTask(selectedPickList.id, taskToComplete.id, {
        pickedQty,
        notes: completeTaskForm.notes || undefined,
      });
      toast.success('Task completed');
      setCompleteTaskDialogOpen(false);
      setTaskToComplete(null);

      // Refresh pick list details
      const response = await yardApi.picking.getById(selectedPickList.id);
      setSelectedPickList(response);
      fetchPickLists();
      fetchStats();
    } catch (error) {
      console.error('Error completing task:', error);
      toast.error('Failed to complete task');
    }
  };

  const handleCompletePickList = async (pickList: YardPickList) => {
    try {
      await yardApi.picking.complete(pickList.id);
      toast.success('Pick list completed');
      fetchPickLists();
      fetchStats();
      if (selectedPickList?.id === pickList.id) {
        setIsSheetOpen(false);
        setSelectedPickList(null);
      }
    } catch (error) {
      console.error('Error completing pick list:', error);
      toast.error('Failed to complete pick list');
    }
  };

  const viewPickListDetails = async (pickList: YardPickList) => {
    try {
      const response = await yardApi.picking.getById(pickList.id);
      setSelectedPickList(response);
      setIsSheetOpen(true);
    } catch (error) {
      console.error('Error fetching pick list details:', error);
      toast.error('Failed to load details');
    }
  };

  const getStatusBadge = (status: PickListStatus) => {
    const config: Record<PickListStatus, { variant: 'default' | 'secondary' | 'destructive' | 'outline'; label: string }> = {
      PENDING: { variant: 'outline', label: 'Pending' },
      IN_PROGRESS: { variant: 'default', label: 'In Progress' },
      COMPLETED: { variant: 'secondary', label: 'Completed' },
      PARTIALLY_COMPLETED: { variant: 'secondary', label: 'Partial' },
      CANCELLED: { variant: 'destructive', label: 'Cancelled' },
    };
    const { variant, label } = config[status] || { variant: 'secondary', label: status };
    return <Badge variant={variant}>{label}</Badge>;
  };

  const calculateProgress = (pickList: YardPickList) => {
    if (!pickList.lines || pickList.lines.length === 0) return 0;
    const completed = pickList.lines.filter(
      (line) => line.status === 'COMPLETED'
    ).length;
    return Math.round((completed / pickList.lines.length) * 100);
  };

  const filteredPickLists = pickLists.filter((pickList) => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesNumber = pickList.pickListNumber?.toLowerCase().includes(query);
      const matchesSource = pickList.sourceType?.toLowerCase().includes(query);
      if (!matchesNumber && !matchesSource) return false;
    }
    return true;
  });

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        title="Picking"
        description="Manage warehouse picking operations"
        actions={
          <Button variant="outline" onClick={() => { fetchPickLists(); fetchStats(); }}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        }
      />

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          label="Pending Pick Lists"
          value={stats.pending}
          icon={<Clock className="h-5 w-5" />}
        />
        <MetricCard
          label="In Progress"
          value={stats.inProgress}
          icon={<Play className="h-5 w-5" />}
        />
        <MetricCard
          label="Completed Today"
          value={stats.completedToday}
          icon={<CheckCircle2 className="h-5 w-5" />}
        />
        <MetricCard
          label="Total Tasks"
          value={stats.totalTasks}
          icon={<ListChecks className="h-5 w-5" />}
        />
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <Input
            placeholder="Search by pick list number or source..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="max-w-md"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="PENDING">Pending</SelectItem>
            <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
            <SelectItem value="COMPLETED">Completed</SelectItem>
            <SelectItem value="PARTIALLY_COMPLETED">Partial</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Pick Lists Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Pick List #</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Tasks</TableHead>
                <TableHead>Progress</TableHead>
                <TableHead>Priority</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Assigned To</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    Loading pick lists...
                  </TableCell>
                </TableRow>
              ) : filteredPickLists.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                    No pick lists found
                  </TableCell>
                </TableRow>
              ) : (
                filteredPickLists.map((pickList) => {
                  const progress = calculateProgress(pickList);
                  return (
                    <TableRow key={pickList.id}>
                      <TableCell className="font-medium">{pickList.pickListNumber}</TableCell>
                      <TableCell>
                        <div>
                          <Badge variant="outline">{pickList.sourceType}</Badge>
                          {pickList.sourceId && (
                            <p className="text-xs text-muted-foreground mt-1">
                              {pickList.sourceId.slice(0, 8)}...
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{pickList.lines?.length || 0} tasks</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 min-w-[100px]">
                          <Progress value={progress} className="h-2" />
                          <span className="text-sm text-muted-foreground">{progress}%</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            pickList.priority <= 3
                              ? 'destructive'
                              : pickList.priority <= 6
                              ? 'outline'
                              : 'secondary'
                          }
                        >
                          {pickList.priority <= 3
                            ? 'High'
                            : pickList.priority <= 6
                            ? 'Medium'
                            : 'Low'}
                        </Badge>
                      </TableCell>
                      <TableCell>{getStatusBadge(pickList.status as PickListStatus)}</TableCell>
                      <TableCell>
                        {pickList.assignedTo ? (
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-muted-foreground" />
                            <span>{pickList.assignedTo.fullName}</span>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">Unassigned</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => viewPickListDetails(pickList)}>
                              View Details
                            </DropdownMenuItem>
                            {pickList.status === 'PENDING' && (
                              <DropdownMenuItem onClick={() => handleStartPickList(pickList)}>
                                <Play className="h-4 w-4 mr-2" />
                                Start Picking
                              </DropdownMenuItem>
                            )}
                            {pickList.status === 'IN_PROGRESS' && (
                              <DropdownMenuItem onClick={() => handleCompletePickList(pickList)}>
                                <CheckCircle2 className="h-4 w-4 mr-2" />
                                Complete Pick List
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pick List Details Sheet */}
      <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
        <SheetContent className="sm:max-w-2xl">
          <SheetHeader>
            <SheetTitle>Pick List Details</SheetTitle>
            <SheetDescription>
              View and execute picking tasks
            </SheetDescription>
          </SheetHeader>
          {selectedPickList && (
            <div className="mt-6 space-y-6">
              {/* Header Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Pick List #</p>
                  <p className="font-medium">{selectedPickList.pickListNumber}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Status</p>
                  {getStatusBadge(selectedPickList.status as PickListStatus)}
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Source</p>
                  <Badge variant="outline">{selectedPickList.sourceType}</Badge>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Priority</p>
                  <Badge
                    variant={
                      selectedPickList.priority <= 3
                        ? 'destructive'
                        : selectedPickList.priority <= 6
                        ? 'outline'
                        : 'secondary'
                    }
                  >
                    {selectedPickList.priority <= 3
                      ? 'High'
                      : selectedPickList.priority <= 6
                      ? 'Medium'
                      : 'Low'}
                  </Badge>
                </div>
              </div>

              {/* Progress */}
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Progress</span>
                  <span>{calculateProgress(selectedPickList)}%</span>
                </div>
                <Progress value={calculateProgress(selectedPickList)} />
              </div>

              {/* Actions */}
              {selectedPickList.status === 'PENDING' && (
                <Button
                  className="w-full"
                  onClick={() => handleStartPickList(selectedPickList)}
                >
                  <Play className="h-4 w-4 mr-2" />
                  Start Picking
                </Button>
              )}

              {/* Pick Tasks */}
              <div className="border-t pt-4">
                <h4 className="font-medium mb-3">
                  Pick Tasks ({selectedPickList.lines?.length || 0})
                </h4>
                {selectedPickList.lines && selectedPickList.lines.length > 0 ? (
                  <div className="space-y-3">
                    {selectedPickList.lines
                      .sort((a, b) => a.sequence - b.sequence)
                      .map((task: YardPickTask, index: number) => (
                        <div
                          key={task.id}
                          className={`p-4 rounded-lg border ${
                            task.status === 'COMPLETED'
                              ? 'bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800'
                              : task.status === 'IN_PROGRESS'
                              ? 'bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800'
                              : 'bg-muted/50'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="text-sm font-medium text-muted-foreground">
                                  #{index + 1}
                                </span>
                                {getStatusBadge(task.status as PickListStatus)}
                              </div>
                              <p className="font-medium">{task.item?.name || 'Unknown Item'}</p>
                              <p className="text-sm text-muted-foreground">{task.item?.sku}</p>

                              <div className="flex items-center gap-4 mt-3 text-sm">
                                <div className="flex items-center gap-1">
                                  <MapPin className="h-4 w-4 text-muted-foreground" />
                                  <span>
                                    {task.storageLocation?.code || 'No location'}
                                  </span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <Package className="h-4 w-4 text-muted-foreground" />
                                  <span>
                                    Qty: {task.pickedQty} / {task.requestedQty}
                                  </span>
                                </div>
                              </div>
                            </div>

                            {selectedPickList.status === 'IN_PROGRESS' &&
                              task.status !== 'COMPLETED' && (
                                <Button
                                  size="sm"
                                  onClick={() => handleOpenCompleteTaskDialog(task)}
                                >
                                  <CheckCircle2 className="h-4 w-4 mr-1" />
                                  Pick
                                </Button>
                              )}

                            {task.status === 'COMPLETED' && (
                              <CheckCircle2 className="h-5 w-5 text-green-600" />
                            )}
                          </div>
                        </div>
                      ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No tasks in this pick list</p>
                )}
              </div>

              {/* Complete Button */}
              {selectedPickList.status === 'IN_PROGRESS' && (
                <Button
                  className="w-full"
                  variant="default"
                  onClick={() => handleCompletePickList(selectedPickList)}
                >
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Complete Pick List
                </Button>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Complete Task Dialog */}
      <Dialog open={completeTaskDialogOpen} onOpenChange={setCompleteTaskDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Complete Pick Task</DialogTitle>
            <DialogDescription>
              Enter the quantity picked from the storage location.
            </DialogDescription>
          </DialogHeader>
          {taskToComplete && (
            <div className="space-y-4 py-4">
              <div className="p-3 bg-muted rounded-lg space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Item</span>
                  <span className="font-medium">{taskToComplete.item?.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Location</span>
                  <span>{taskToComplete.storageLocation?.code}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Requested</span>
                  <span>{taskToComplete.requestedQty}</span>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="pickedQty">Picked Quantity *</Label>
                <Input
                  id="pickedQty"
                  type="number"
                  min="0"
                  max={taskToComplete.requestedQty}
                  value={completeTaskForm.pickedQty}
                  onChange={(e) =>
                    setCompleteTaskForm((prev) => ({ ...prev, pickedQty: e.target.value }))
                  }
                />
                {parseFloat(completeTaskForm.pickedQty) < taskToComplete.requestedQty && (
                  <p className="text-sm text-amber-600 flex items-center gap-1">
                    <AlertCircle className="h-4 w-4" />
                    Short pick - picking less than requested
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="taskNotes">Notes</Label>
                <Textarea
                  id="taskNotes"
                  value={completeTaskForm.notes}
                  onChange={(e) =>
                    setCompleteTaskForm((prev) => ({ ...prev, notes: e.target.value }))
                  }
                  placeholder="Any notes about this pick..."
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompleteTaskDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCompleteTask}>
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Confirm Pick
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
